import { useState } from "react";
import Spinner from "../../components/ui/Spinner";
import { Help, btnGhost, btnPrimary, cardCls, errMsg, inputCls, labelCls } from "../../components/admin/promotionsUi";
import { formatISTDateTime } from "../../utils/dateUtils";
import { gamificationService } from "../../features/gamification/service";
import type { GamificationUserView, UserGrant, UserSearchResult, WalletLotsView } from "../../features/gamification/types";
import { LotsTable, PersonCell, useCreditRate } from "./GamificationPanels";

const SHARE_VISIT_REASON: Record<string, string> = {
  counted: "Counted",
  owner: "Opened by the owner",
  repeat_today: "Same visitor again today",
  daily_cap: "Daily limit for this link reached",
  account_too_new: "Visitor's account is too new",
  signed_out_visitor: "Visitor was not signed in",
  blocked_same_network_device: "From the owner's own network",
  rate_limited: "Too many opens at once",
};

// ── User look-up ─────────────────────────────────────────────────────────────
const yesNo = (v: boolean) => (v ? "Yes" : "No");

const MIN_SEARCH = 3;

export default function GamificationUserLookupPanel() {
  const [query, setQuery] = useState("");
  const [searched, setSearched] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [results, setResults] = useState<UserSearchResult[] | null>(null);
  const [userId, setUserId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [view, setView] = useState<GamificationUserView | null>(null);
  const [lots, setLots] = useState<WalletLotsView | null>(null);
  const [lotsError, setLotsError] = useState("");
  const rate = useCreditRate();

  const search = async (text: string) => {
    const q = text.trim();
    if (q.length < MIN_SEARCH) {
      setResults(null);
      setSearchError(`Type at least ${MIN_SEARCH} characters of a name, email or phone number.`);
      return;
    }
    setSearching(true);
    setSearchError("");
    setSearched(q);
    try {
      setResults(await gamificationService.searchUsers(q, 10));
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setResults(null);
      setSearchError(
        status === 422
          ? `Type at least ${MIN_SEARCH} characters of a name, email or phone number.`
          : "Couldn't search for users right now. Please try again.",
      );
    } finally {
      setSearching(false);
    }
  };

  const lookup = async (rawId: string) => {
    const id = rawId.trim();
    if (!id) return;
    setBusy(true);
    setError("");
    setView(null);
    setLots(null);
    setLotsError("");
    try {
      setView(await gamificationService.getUser(id));
      // The credit lots come from the wallet; their failure never hides the rewards view.
      gamificationService.getWalletLots(id).then(setLots).catch((e) => setLotsError(errMsg(e, "Couldn't load this user's credit records.")));
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setError(status === 404 ? "No user found with that id. Check the id and try again." : "Couldn't load that user right now. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const grants: UserGrant[] = view?.grants ?? [];
  const progress = view?.progress ?? null;

  return (
    <section className={cardCls}>
      <h2 className="text-white font-semibold mb-1">Look up a user</h2>
      <p className="text-gray-500 text-xs mb-4">
        Find a user and see what they have earned and how far along they are. Read-only.
        <span className="block text-gray-600">Example: search the email from a support ticket to check why a reward did or did not arrive.</span>
      </p>
      <form className="mb-3" onSubmit={(e) => { e.preventDefault(); void search(query); }}>
        <label className={labelCls} htmlFor="gamification-user-search">Search by name, email or phone</label>
        <div className="flex flex-wrap gap-2">
          <input id="gamification-user-search" className={`${inputCls} max-w-sm`} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Name, email or phone" />
          <button type="submit" disabled={searching || query.trim().length < MIN_SEARCH} className={btnPrimary}>{searching ? "Searching…" : "Search"}</button>
        </div>
        <Help>At least {MIN_SEARCH} characters. Shows up to 10 matches.</Help>
      </form>
      {searching && <div className="flex justify-start py-2"><Spinner /></div>}
      {searchError && (
        <div className="flex flex-wrap items-center gap-3 mb-3" role="alert">
          <p className="text-red-400 text-sm">{searchError}</p>
          {searchError.startsWith("Couldn't") && <button onClick={() => void search(searched || query)} className={btnGhost}>Retry</button>}
        </div>
      )}
      {!searching && results && results.length === 0 && (
        <p className="text-gray-500 text-sm mb-3">No users match "{searched}".</p>
      )}
      {!searching && results && results.length > 0 && (
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-xs text-gray-500">
                <th className="pb-2 pr-3 font-medium">Name</th>
                <th className="pb-2 pr-3 font-medium">Email</th>
                <th className="pb-2 pr-3 font-medium">Phone</th>
                <th className="pb-2 pr-3 font-medium">Role</th>
                <th className="pb-2 pr-3 font-medium">Onboarding done</th>
                <th className="pb-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {results.map((u) => (
                <tr key={u.id} className="border-t border-gray-800 text-gray-300">
                  <td className="py-2 pr-3">{u.name || "—"}</td>
                  <td className="py-2 pr-3">{u.email || "—"}</td>
                  <td className="py-2 pr-3">{u.phone || "—"}</td>
                  <td className="py-2 pr-3 capitalize">{u.role || "—"}</td>
                  <td className="py-2 pr-3">{yesNo(u.onboarding_completed)}</td>
                  <td className="py-2"><button onClick={() => { setUserId(u.id); void lookup(u.id); }} disabled={busy} className={btnGhost}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <form className="flex flex-wrap items-center gap-2 mb-4" onSubmit={(e) => { e.preventDefault(); void lookup(userId); }}>
        <label className="text-xs text-gray-500" htmlFor="gamification-user-id">or paste a user id</label>
        <input id="gamification-user-id" className={`${inputCls} max-w-xs`} value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="User id" />
        <button type="submit" disabled={busy || !userId.trim()} className={btnGhost}>{busy ? "Loading…" : "View"}</button>
      </form>
      {error && (
        <div className="flex flex-wrap items-center gap-3" role="alert">
          <p className="text-red-400 text-sm">{error}</p>
          {error.startsWith("Couldn't") && <button onClick={() => void lookup(userId)} className={btnGhost}>Retry</button>}
        </div>
      )}
      {view && (
        <div className="space-y-5">
          <div className="text-sm text-gray-300">
            <PersonCell id={userId.trim() || null} name={view.user_name} email={view.user_email} phone={view.user_phone} />
          </div>
          <div>
            <h3 className="text-gray-200 text-sm font-semibold mb-2">Grants</h3>
            {grants.length === 0 ? (
              <p className="text-gray-500 text-sm">This user has not been granted any credits yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500">
                      <th className="pb-2 pr-3 font-medium">Quest</th>
                      <th className="pb-2 pr-3 font-medium">Credits</th>
                      <th className="pb-2 pr-3 font-medium">Status</th>
                      <th className="pb-2 pr-3 font-medium">Granted</th>
                      <th className="pb-2 font-medium">Expires</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grants.map((g, i) => (
                      <tr key={`${g.quest_id ?? i}-${i}`} className="border-t border-gray-800 text-gray-300">
                        <td className="py-2 pr-3">{g.quest_name ?? g.name ?? g.quest_id ?? "—"}</td>
                        <td className="py-2 pr-3">{g.credits ?? g.amount ?? "—"}</td>
                        <td className="py-2 pr-3">{g.status ?? "—"}</td>
                        <td className="py-2 pr-3 whitespace-nowrap">{formatISTDateTime(g.granted_at ?? g.created_at)}</td>
                        <td className="py-2 whitespace-nowrap">{formatISTDateTime(g.expires_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div>
            <h3 className="text-gray-200 text-sm font-semibold mb-2">Progress</h3>
            {!progress ? (
              <p className="text-gray-500 text-sm">No quest progress recorded for this user.</p>
            ) : (
              <div className="space-y-4">
                <p className="text-gray-400 text-xs">
                  Rewards {progress.enabled ? "on" : "off"} · {progress.eligible ? "eligible" : "not eligible"} · onboarding {progress.onboarding_completed ? "completed" : "not completed"} · checklist {progress.checklist.percent}% done
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="text-xs text-gray-500">
                        <th className="pb-2 pr-3 font-medium">Checklist step</th>
                        <th className="pb-2 pr-3 font-medium">Done</th>
                        <th className="pb-2 pr-3 font-medium">Granted</th>
                        <th className="pb-2 font-medium">Reward</th>
                      </tr>
                    </thead>
                    <tbody>
                      {progress.checklist.steps.map((st) => (
                        <tr key={st.key} className="border-t border-gray-800 text-gray-300">
                          <td className="py-2 pr-3">{st.title}</td>
                          <td className="py-2 pr-3">{yesNo(st.done)}</td>
                          <td className="py-2 pr-3">{yesNo(st.granted)}</td>
                          <td className="py-2">{st.reward}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {progress.quests.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead>
                        <tr className="text-xs text-gray-500">
                          <th className="pb-2 pr-3 font-medium">Quest</th>
                          <th className="pb-2 pr-3 font-medium">Done</th>
                          <th className="pb-2 pr-3 font-medium">Granted</th>
                          <th className="pb-2 font-medium">Reward</th>
                        </tr>
                      </thead>
                      <tbody>
                        {progress.quests.map((q) => (
                          <tr key={q.quest_id} className="border-t border-gray-800 text-gray-300">
                            <td className="py-2 pr-3">{q.name}{!q.enabled && <span className="text-gray-500 text-xs"> (off)</span>}</td>
                            <td className="py-2 pr-3">{yesNo(q.done)}</td>
                            <td className="py-2 pr-3">{yesNo(q.granted)}</td>
                            <td className="py-2">{q.reward}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="text-gray-300 text-sm">
                  Total granted: {progress.total_granted_credits} credits · Potential still to earn: {progress.potential_credits} credits
                </p>
              </div>
            )}
          </div>
          <div>
            <h3 className="text-gray-200 text-sm font-semibold mb-2">Wallet credits</h3>
            {lotsError ? (
              <p className="text-red-400 text-sm" role="alert">{lotsError}</p>
            ) : !lots ? (
              <div className="flex justify-start py-2"><Spinner /></div>
            ) : (
              <>
                <p className="text-gray-400 text-xs mb-2">Each credit with its source and expiry. To take one back, use the Take-backs tab.</p>
                <LotsTable lots={lots.lots} rate={rate} />
              </>
            )}
          </div>
          <div>
            <h3 className="text-gray-200 text-sm font-semibold mb-2">Share link</h3>
            {view.share ? (
              <>
                <p className="text-gray-300 text-sm">Code <span className="font-mono">{view.share.code}</span> · {view.share.total_visits ?? 0} visit{(view.share.total_visits ?? 0) === 1 ? "" : "s"} · {view.share.counted_hits} counted{view.share.first_hit_at ? ` · first counted on ${formatISTDateTime(view.share.first_hit_at)}` : ""}</p>
                {(view.share.recent_visits ?? []).length > 0 && (
                  <ul className="mt-2 space-y-1 text-xs text-gray-400">
                    {(view.share.recent_visits ?? []).map((v, i) => (
                      <li key={i}>{v.created_at ? formatISTDateTime(v.created_at) : "—"} · {SHARE_VISIT_REASON[v.reason] ?? "Not counted"}{v.visitor_kind ? ` (${v.visitor_kind === "signed-in" ? "signed in" : "not signed in"})` : ""}</li>
                    ))}
                  </ul>
                )}
              </>
            ) : (
              <p className="text-gray-500 text-sm">This user has no share code yet.</p>
            )}
          </div>
          <div>
            <h3 className="text-gray-200 text-sm font-semibold mb-2">Badges</h3>
            {(view.badges as { badge_id: string; name?: string; awarded_at?: string | null }[]).length === 0 ? (
              <p className="text-gray-500 text-sm">No badges yet.</p>
            ) : (
              <ul className="text-gray-300 text-sm space-y-1">
                {(view.badges as { badge_id: string; name?: string; awarded_at?: string | null }[]).map((b) => (
                  <li key={b.badge_id}>{b.name || b.badge_id}{b.awarded_at ? <span className="text-gray-500"> · awarded {formatISTDateTime(b.awarded_at)}</span> : null}</li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <h3 className="text-gray-200 text-sm font-semibold mb-2">Recent messages sent</h3>
            {(view.notifications ?? []).length === 0 ? (
              <p className="text-gray-500 text-sm">No reward messages sent to this user.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500">
                      <th className="pb-2 pr-3 font-medium">Sent</th>
                      <th className="pb-2 pr-3 font-medium">Message</th>
                      <th className="pb-2 font-medium">Read</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(view.notifications ?? []).map((n) => (
                      <tr key={n.id} className="border-t border-gray-800 text-gray-300">
                        <td className="py-2 pr-3 whitespace-nowrap">{formatISTDateTime(n.created_at)}</td>
                        <td className="py-2 pr-3"><span className="text-white">{n.title}</span>{n.body ? <span className="block text-gray-500 text-xs">{n.body}</span> : null}</td>
                        <td className="py-2">{yesNo(n.read)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
